import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Zap } from 'lucide-react';
import { apiClient } from '@/lib/apiClient';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { CreateOpportunityModal } from '@/components/CreateOpportunityModal';
import { NewLeadModal } from '@/components/NewLeadModal';
import { useAuthStore } from '@/stores/authStore';
import { canWriteCampaigns } from '@/lib/campaignAccess';

interface ContentVariant {
  variantKey: string;
  subjectLine: string;
  body: string;
  ctaCopy: string;
}
interface CampaignContentOutput {
  variants: ContentVariant[];
  selectedVariantIndex: number;
}

interface CampaignSummary {
  id: string;
  name: string;
  description: string | null;
  status: string;
  startsAt: string | null;
  endsAt: string | null;
}

interface PerformanceSummary {
  leadCountByStatus: Record<string, number>;
  totalLeads: number;
  qualificationRate: number;
  opportunityCountByStatus: Record<string, number>;
  openPipelineValue: number;
  weightedForecast: number;
  winRate: number;
}

interface CampaignLeadRow {
  id: string;
  status: string;
  source: string;
  channel: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  companyName: string | null;
  qualificationNote: string | null;
  createdAt: string | null;
}

interface CampaignAccountRow {
  id: string;
  name: string;
  industry: string | null;
  status: string;
}

interface CampaignContactRow {
  id: string;
  accountId: string | null;
  accountName: string | null;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string | null;
  title: string | null;
}

interface CampaignOpportunityRow {
  id: string;
  name: string;
  status: string;
  estimatedDealValue: number;
  currency: string;
  progressPercent: number;
  forecastClassification: string;
  expectedCloseDate: string | null;
  accountId: string;
  sourceLeadId: string | null;
  createdAt: string | null;
}

interface AssignmentRow {
  campaignId: string;
  repUserId: string;
  quotaAccounts: number | null;
}

const STATUS_BADGE: Record<string, string> = {
  DRAFT: 'bg-muted text-muted-foreground',
  ACTIVE: 'bg-green-900/40 text-green-200',
  PAUSED: 'bg-amber-900/40 text-amber-200',
  CLOSED: 'bg-blue-900/40 text-blue-200',
};

const TABS = ['leads', 'accounts', 'contacts', 'opportunities', 'performance', 'reps'] as const;
type Tab = (typeof TABS)[number];

/**
 * Story 46.2 — campaign detail page as a hub. Top-level entry point
 * for managers/admins drilling into a single campaign.
 */
export function CampaignDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const role = useAuthStore((s) => s.user?.role);
  // VYV-K7.2 — the same allow-set the backend enforces on campaign writes
  // (internal/campaigns/authz.go). Unchanged set, single source of truth.
  const isElevated = canWriteCampaigns(role);
  const [campaign, setCampaign] = useState<CampaignSummary | null>(null);
  // Campaign Content AI state (Epic 66.2)
  const [contentDraft, setContentDraft] = useState<CampaignContentOutput | null>(null);
  const [contentLoading, setContentLoading] = useState(false);
  const [contentError, setContentError] = useState<string | null>(null);
  const [perf, setPerf] = useState<PerformanceSummary | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>('leads');
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setNotFound(false);
    Promise.all([
      apiClient.get<CampaignSummary>(`/campaigns/${id}`),
      apiClient.get<PerformanceSummary>(`/campaigns/${id}/performance`),
    ])
      .then(([c, p]) => {
        setCampaign(c.data);
        setPerf(p.data);
      })
      .catch((err) => {
        if (err?.response?.status === 404) setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <p className="p-6 text-sm text-muted-foreground">{t('common.loading')}</p>;
  if (notFound || !campaign)
    return (
      <div className="p-6">
        <Link to="/campaigns" className="inline-flex items-center gap-1 text-sm text-primary">
          <ArrowLeft className="h-4 w-4" /> {t('nav.campaigns')}
        </Link>
        <p className="mt-4 text-sm text-muted-foreground">{t('campaign.not_found')}</p>
      </div>
    );

  async function generateCampaignContent() {
    if (!campaign) return;
    setContentLoading(true);
    setContentError(null);
    try {
      const r = await apiClient.post('/api/v1/agents/A14_CAMPAIGN_CONTENT/run', {
        input: {
          objective: campaign.description ?? campaign.name,
          targetAudience: 'B2B prospects',
          channel: 'email',
          variantCount: 3,
        },
      });
      setContentDraft(r.data?.output ?? r.data);
    } catch {
      setContentError(t('campaignContent.error'));
    } finally {
      setContentLoading(false);
    }
  }

  return (
    <div className="space-y-6 p-6">
      <Breadcrumbs
        crumbs={
          isElevated
            ? [{ label: t('nav.portfolio'), to: '/portfolio' }, { label: campaign.name }]
            : [{ label: t('nav.campaigns'), to: '/campaigns' }, { label: campaign.name }]
        }
      />
      <Link
        to={isElevated ? '/portfolio' : '/campaigns'}
        className="inline-flex items-center gap-1 text-sm text-primary"
      >
        <ArrowLeft className="h-4 w-4" />{' '}
        {isElevated ? t('nav.portfolio') : t('nav.campaigns')}
      </Link>

      {/* Header */}
      <Card>
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-semibold">{campaign.name}</h1>
              <span
                className={`rounded-full px-3 py-0.5 text-xs font-medium ${
                  STATUS_BADGE[campaign.status] ?? 'bg-muted'
                }`}
              >
                {campaign.status}
              </span>
            </div>
            {campaign.description && (
              <p className="mt-2 text-sm text-muted-foreground">{campaign.description}</p>
            )}
            <div className="mt-3 flex flex-wrap gap-4 text-xs text-muted-foreground">
              <span>{t('campaign.starts')}: {campaign.startsAt ?? '—'}</span>
              <span>{t('campaign.ends')}: {campaign.endsAt ?? '—'}</span>
            </div>
          </div>
          {isElevated && (
            <Button size="sm" variant="outline" onClick={() => setEditOpen(true)}>
              {t('common.edit')}
            </Button>
          )}
        </div>

        {/* KPI strip */}
        {perf && (
          <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-5">
            <Kpi label={t('campaign.kpi.leads')} value={perf.totalLeads.toLocaleString()} />
            <Kpi
              label={t('campaign.kpi.qualified_rate')}
              value={`${(perf.qualificationRate * 100).toFixed(1)}%`}
            />
            <Kpi
              label={t('campaign.kpi.open_opps')}
              value={(perf.opportunityCountByStatus.OPEN ?? 0).toString()}
            />
            <Kpi
              label={t('campaign.kpi.weighted_forecast')}
              value={formatCurrency(perf.weightedForecast)}
            />
            <Kpi
              label={t('campaign.kpi.win_rate')}
              value={`${(perf.winRate * 100).toFixed(1)}%`}
            />
          </div>
        )}
      </Card>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 border-b">
        {TABS.map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => setActiveTab(tab)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm transition ${
              activeTab === tab
                ? 'border-primary font-medium text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {t(`campaign.tab.${tab}`)}
          </button>
        ))}
      </div>

      {activeTab === 'leads' && id && <LeadsTab campaignId={id} navigate={navigate} />}
      {activeTab === 'accounts' && id && <AccountsTab campaignId={id} navigate={navigate} />}
      {activeTab === 'contacts' && id && <ContactsTab campaignId={id} />}
      {activeTab === 'opportunities' && id && (
        <OpportunitiesTab campaignId={id} navigate={navigate} />
      )}
      {activeTab === 'performance' && perf && <PerformanceTab perf={perf} />}
      {activeTab === 'reps' && id && <RepsTab campaignId={id} />}
      {editOpen && (
        <EditCampaignDialog
          campaign={campaign}
          onClose={() => setEditOpen(false)}
          onSaved={(updated) => {
            setCampaign(updated);
            setEditOpen(false);
          }}
        />
      )}

      {/* AI Campaign Content card (Epic 66.2 + 66.3) */}
      <Card className="p-6">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {t('campaignContent.title')}
          </h2>
          <Button
            size="sm"
            variant="outline"
            onClick={generateCampaignContent}
            disabled={contentLoading}
          >
            <Zap className="mr-1 h-3.5 w-3.5" />
            {contentLoading ? t('campaignContent.generating') : t('campaignContent.generate_btn')}
          </Button>
        </div>
        {contentError && (
          <p className="mt-2 text-xs text-destructive">{contentError}</p>
        )}
        {contentDraft && (
          <div className="mt-4 space-y-4">
            <p className="text-xs text-muted-foreground">
              {t('campaignContent.variants_count', { n: contentDraft.variants.length })}
            </p>
            {contentDraft.variants.map((v, i) => (
              <div
                key={v.variantKey}
                className={`rounded border p-3 text-sm ${
                  i === contentDraft.selectedVariantIndex
                    ? 'border-primary bg-primary/5'
                    : 'border-border'
                }`}
              >
                <div className="flex items-center justify-between">
                  <p className="font-semibold">
                    {t('campaignContent.variant_label', { key: v.variantKey })}
                  </p>
                  {i === contentDraft.selectedVariantIndex && (
                    <span className="rounded bg-primary/10 px-2 py-0.5 text-xs text-primary">
                      {t('campaignContent.selected')}
                    </span>
                  )}
                </div>
                <p className="mt-1 font-medium">{v.subjectLine}</p>
                <p className="mt-1 text-muted-foreground">{v.body}</p>
                <p className="mt-1 text-xs text-primary">CTA: {v.ctaCopy}</p>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function EditCampaignDialog({
  campaign,
  onClose,
  onSaved,
}: {
  campaign: CampaignSummary;
  onClose: () => void;
  onSaved: (updated: CampaignSummary) => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(campaign.name);
  const [description, setDescription] = useState(campaign.description ?? '');
  const [status, setStatus] = useState(campaign.status);
  const [startsAt, setStartsAt] = useState(campaign.startsAt ?? '');
  const [endsAt, setEndsAt] = useState(campaign.endsAt ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    const payload: Record<string, unknown> = {};
    if (name.trim() !== campaign.name) payload.name = name.trim();
    if (description !== (campaign.description ?? '')) {
      payload.description = description.trim() || null;
    }
    if (status !== campaign.status) payload.status = status;
    if (startsAt !== (campaign.startsAt ?? '')) payload.startsAt = startsAt || null;
    if (endsAt !== (campaign.endsAt ?? '')) payload.endsAt = endsAt || null;
    try {
      const { data } = await apiClient.patch<CampaignSummary>(
        `/campaigns/${campaign.id}`,
        payload,
      );
      onSaved(data);
    } catch {
      setError(t('campaign.edit_error'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-lg rounded-lg border bg-card p-6 shadow-xl">
        <h3 className="mb-4 text-lg font-semibold">{t('campaign.edit_title')}</h3>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="space-y-1">
            <label className="text-sm font-medium">{t('campaign.field.name')}</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-md border bg-transparent px-3 py-2 text-sm"
              required
            />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium">{t('campaign.field.description')}</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full rounded-md border bg-transparent px-3 py-2 text-sm"
            />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium">{t('campaign.field.status')}</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full rounded-md border bg-transparent px-3 py-2 text-sm"
            >
              <option value="DRAFT">DRAFT</option>
              <option value="ACTIVE">ACTIVE</option>
              <option value="PAUSED">PAUSED</option>
              <option value="CLOSED">CLOSED</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-sm font-medium">{t('campaign.field.starts_at')}</label>
              <input
                type="date"
                value={startsAt}
                onChange={(e) => setStartsAt(e.target.value)}
                className="w-full rounded-md border bg-transparent px-3 py-2 text-sm"
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">{t('campaign.field.ends_at')}</label>
              <input
                type="date"
                value={endsAt}
                onChange={(e) => setEndsAt(e.target.value)}
                className="w-full rounded-md border bg-transparent px-3 py-2 text-sm"
              />
            </div>
          </div>
          {error && (
            <div className="rounded-md border border-red-800 bg-red-950/40 px-3 py-2 text-sm text-red-300">
              {error}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" size="sm" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" size="sm" disabled={!name.trim() || submitting}>
              {submitting ? t('common.saving') : t('common.save')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md bg-muted/40 p-3">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-1 text-xl font-semibold">{value}</div>
    </div>
  );
}

function formatCurrency(n: number): string {
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

function LeadsTab({ campaignId, navigate }: { campaignId: string; navigate: ReturnType<typeof useNavigate> }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<CampaignLeadRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshToken, setRefreshToken] = useState(0);
  const [newLeadOpen, setNewLeadOpen] = useState(false);
  useEffect(() => {
    apiClient
      .get<CampaignLeadRow[]>(`/campaigns/${campaignId}/leads`)
      .then(({ data }) => setRows(data))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [campaignId, refreshToken]);
  if (loading) return <p className="text-sm text-muted-foreground">{t('common.loading')}</p>;
  const modal = (
    <NewLeadModal
      open={newLeadOpen}
      onClose={() => setNewLeadOpen(false)}
      onCreated={() => setRefreshToken((x) => x + 1)}
      lockedCampaignId={campaignId}
    />
  );
  if (rows.length === 0)
    return (
      <>
        <Card className="space-y-3 text-center">
          <p className="text-sm text-muted-foreground">{t('campaign.empty.leads')}</p>
          <div className="flex justify-center">
            <Button size="sm" onClick={() => setNewLeadOpen(true)}>
              {t('campaign.add_lead')}
            </Button>
          </div>
        </Card>
        {modal}
      </>
    );
  return (
    <div className="space-y-3">
    <div className="flex justify-end">
      <Button size="sm" onClick={() => setNewLeadOpen(true)}>
        {t('campaign.add_lead')}
      </Button>
    </div>
    <Card className="overflow-hidden p-0">
      <table className="w-full text-sm">
        <thead className="bg-muted text-xs uppercase tracking-wide text-muted-foreground">
          <tr>
            <th className="px-4 py-2 text-left">{t('campaign.table.company')}</th>
            <th className="px-4 py-2 text-left">{t('campaign.table.contact')}</th>
            <th className="px-4 py-2 text-left">{t('campaign.table.phone')}</th>
            <th className="px-4 py-2 text-left">{t('campaign.table.status')}</th>
            <th className="px-4 py-2 text-left">{t('campaign.table.channel')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.id}
              onClick={() => navigate(`/leads/${r.id}`)}
              className="cursor-pointer border-t hover:bg-muted/50"
            >
              <td className="px-4 py-2 font-medium">{r.companyName ?? '—'}</td>
              <td className="px-4 py-2 text-muted-foreground">
                {[r.firstName, r.lastName].filter(Boolean).join(' ') || '—'}
              </td>
              <td className="px-4 py-2">{r.phone ?? '—'}</td>
              <td className="px-4 py-2">
                <span className="rounded-full bg-muted px-2 py-0.5 text-xs">{r.status}</span>
              </td>
              <td className="px-4 py-2 text-xs text-muted-foreground">{r.channel ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
    {modal}
    </div>
  );
}

function AccountsTab({ campaignId, navigate }: { campaignId: string; navigate: ReturnType<typeof useNavigate> }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<CampaignAccountRow[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    apiClient
      .get<CampaignAccountRow[]>(`/campaigns/${campaignId}/accounts`)
      .then(({ data }) => setRows(data))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [campaignId]);
  if (loading) return <p className="text-sm text-muted-foreground">{t('common.loading')}</p>;
  if (rows.length === 0) return <Card>{t('campaign.empty.accounts')}</Card>;
  return (
    <Card className="overflow-hidden p-0">
      <table className="w-full text-sm">
        <thead className="bg-muted text-xs uppercase tracking-wide text-muted-foreground">
          <tr>
            <th className="px-4 py-2 text-left">{t('campaign.table.name')}</th>
            <th className="px-4 py-2 text-left">{t('campaign.table.industry')}</th>
            <th className="px-4 py-2 text-left">{t('campaign.table.status')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.id}
              onClick={() => navigate(`/accounts/${r.id}`)}
              className="cursor-pointer border-t hover:bg-muted/50"
            >
              <td className="px-4 py-2 font-medium">{r.name}</td>
              <td className="px-4 py-2 text-muted-foreground">{r.industry ?? '—'}</td>
              <td className="px-4 py-2">{r.status}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function ContactsTab({ campaignId }: { campaignId: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [rows, setRows] = useState<CampaignContactRow[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    apiClient
      .get<CampaignContactRow[]>(`/campaigns/${campaignId}/contacts`)
      .then(({ data }) => setRows(data))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [campaignId]);
  if (loading) return <p className="text-sm text-muted-foreground">{t('common.loading')}</p>;
  if (rows.length === 0) return <Card>{t('campaign.empty.contacts')}</Card>;
  return (
    <Card className="overflow-hidden p-0">
      <table className="w-full text-sm">
        <thead className="bg-muted text-xs uppercase tracking-wide text-muted-foreground">
          <tr>
            <th className="px-4 py-2 text-left">{t('campaign.table.name')}</th>
            <th className="px-4 py-2 text-left">{t('campaign.table.title')}</th>
            <th className="px-4 py-2 text-left">{t('campaign.table.account')}</th>
            <th className="px-4 py-2 text-left">{t('campaign.table.phone')}</th>
            <th className="px-4 py-2 text-left">{t('campaign.table.email')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => (
            <tr
              key={c.id}
              onClick={() =>
                c.accountId &&
                navigate(`/accounts/${c.accountId}?focusContact=${c.id}`)
              }
              className={`border-t ${
                c.accountId ? 'cursor-pointer hover:bg-muted/50' : ''
              }`}
              title={c.accountId ? t('contact.open_in_account') : undefined}
            >
              <td className="px-4 py-2 font-medium">
                {c.firstName} {c.lastName}
              </td>
              <td className="px-4 py-2 text-muted-foreground">{c.title ?? '—'}</td>
              <td
                className="px-4 py-2"
                onClick={(e) => e.stopPropagation()}
              >
                {c.accountId ? (
                  <Link to={`/accounts/${c.accountId}`} className="text-primary hover:underline">
                    {c.accountName ?? '—'}
                  </Link>
                ) : (
                  '—'
                )}
              </td>
              <td className="px-4 py-2">{c.phone ?? '—'}</td>
              <td className="px-4 py-2">{c.email ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function OpportunitiesTab({ campaignId, navigate }: { campaignId: string; navigate: ReturnType<typeof useNavigate> }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<CampaignOpportunityRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshToken, setRefreshToken] = useState(0);
  const [newOppOpen, setNewOppOpen] = useState(false);
  useEffect(() => {
    apiClient
      .get<CampaignOpportunityRow[]>(`/campaigns/${campaignId}/opportunities`)
      .then(({ data }) => setRows(data))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [campaignId, refreshToken]);
  if (loading) return <p className="text-sm text-muted-foreground">{t('common.loading')}</p>;
  const modal = (
    <CreateOpportunityModal
      open={newOppOpen}
      onClose={() => setNewOppOpen(false)}
      onCreated={() => setRefreshToken((x) => x + 1)}
      preselectedCampaignId={campaignId}
    />
  );
  if (rows.length === 0)
    return (
      <>
        <Card className="space-y-3 text-center">
          <p className="text-sm text-muted-foreground">{t('campaign.empty.opportunities')}</p>
          <div className="flex justify-center">
            <Button size="sm" onClick={() => setNewOppOpen(true)}>
              {t('campaign.add_opportunity')}
            </Button>
          </div>
        </Card>
        {modal}
      </>
    );
  return (
    <div className="space-y-3">
    <div className="flex justify-end">
      <Button size="sm" onClick={() => setNewOppOpen(true)}>
        {t('campaign.add_opportunity')}
      </Button>
    </div>
    <Card className="overflow-hidden p-0">
      <table className="w-full text-sm">
        <thead className="bg-muted text-xs uppercase tracking-wide text-muted-foreground">
          <tr>
            <th className="px-4 py-2 text-left">{t('campaign.table.name')}</th>
            <th className="px-4 py-2 text-right">{t('campaign.table.value')}</th>
            <th className="px-4 py-2 text-right">{t('campaign.table.progress')}</th>
            <th className="px-4 py-2 text-left">{t('campaign.table.forecast')}</th>
            <th className="px-4 py-2 text-left">{t('campaign.table.status')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((o) => (
            <tr
              key={o.id}
              onClick={() => navigate(`/opportunities/${o.id}`)}
              className="cursor-pointer border-t hover:bg-muted/50"
            >
              <td className="px-4 py-2 font-medium">{o.name}</td>
              <td className="px-4 py-2 text-right font-mono">
                {formatCurrency(o.estimatedDealValue)}
              </td>
              <td className="px-4 py-2 text-right">{Number(o.progressPercent).toFixed(0)}%</td>
              <td className="px-4 py-2 text-xs">{o.forecastClassification}</td>
              <td className="px-4 py-2">
                <span className="rounded-full bg-muted px-2 py-0.5 text-xs">{o.status}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
    {modal}
    </div>
  );
}

function PerformanceTab({ perf }: { perf: PerformanceSummary }) {
  const { t } = useTranslation();
  const leadStatuses = ['NEW', 'QUALIFIED', 'CONVERTED', 'DISCARDED'];
  const oppStatuses = ['OPEN', 'WON', 'LOST'];
  const maxLeads = Math.max(1, ...Object.values(perf.leadCountByStatus));
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <Card>
        <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {t('campaign.performance.lead_funnel')}
        </h2>
        <div className="space-y-2">
          {leadStatuses.map((s) => {
            const n = perf.leadCountByStatus[s] ?? 0;
            return (
              <div key={s}>
                <div className="flex justify-between text-sm">
                  <span>{s}</span>
                  <span className="font-mono text-muted-foreground">{n}</span>
                </div>
                <div className="h-1.5 w-full rounded-full bg-muted">
                  <div
                    className="h-1.5 rounded-full bg-primary"
                    style={{ width: `${(n / maxLeads) * 100}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </Card>
      <Card>
        <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {t('campaign.performance.opp_status')}
        </h2>
        <div className="space-y-2">
          {oppStatuses.map((s) => (
            <div key={s} className="flex justify-between text-sm">
              <span>{s}</span>
              <span className="font-mono">{perf.opportunityCountByStatus[s] ?? 0}</span>
            </div>
          ))}
          <div className="mt-3 border-t pt-3 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('portfolio.stat.open_pipeline')}</span>
              <span className="font-mono">{formatCurrency(perf.openPipelineValue)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('campaign.kpi.weighted_forecast')}</span>
              <span className="font-mono">{formatCurrency(perf.weightedForecast)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('campaign.kpi.win_rate')}</span>
              <span className="font-mono">{(perf.winRate * 100).toFixed(1)}%</span>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}

interface UserSummary {
  id: string;
  email: string;
  role: string;
  status: string;
}

function RepsTab({ campaignId }: { campaignId: string }) {
  const { t } = useTranslation();
  // VYV-K7.2 — POST /campaigns/{id}/assignments is a write: only offer it to
  // roles the backend lets through.
  const canWrite = canWriteCampaigns(useAuthStore((s) => s.user?.role));
  const [rows, setRows] = useState<AssignmentRow[]>([]);
  const [users, setUsers] = useState<UserSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshToken, setRefreshToken] = useState(0);
  const [assignOpen, setAssignOpen] = useState(false);
  useEffect(() => {
    Promise.all([
      apiClient.get<AssignmentRow[]>(`/campaigns/${campaignId}/assignments`),
      apiClient.get<UserSummary[]>('/users'),
    ])
      .then(([a, u]) => {
        setRows(a.data);
        setUsers(u.data);
      })
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [campaignId, refreshToken]);
  if (loading) return <p className="text-sm text-muted-foreground">{t('common.loading')}</p>;
  const userById = new Map(users.map((u) => [u.id, u]));
  const eligibleReps = users.filter(
    (u) =>
      u.status === 'ACTIVE' &&
      (u.role === 'SALES_REPRESENTATIVE' || u.role === 'SALES_MANAGER'),
  );
  const assignedIds = new Set(rows.map((r) => r.repUserId));
  const candidates = eligibleReps.filter((u) => !assignedIds.has(u.id));
  const modal = (
    <AssignRepDialog
      open={assignOpen}
      onClose={() => setAssignOpen(false)}
      onAssigned={() => setRefreshToken((x) => x + 1)}
      campaignId={campaignId}
      candidates={candidates}
    />
  );
  if (rows.length === 0)
    return (
      <>
        <Card className="space-y-3 text-center">
          <p className="text-sm text-muted-foreground">{t('campaign.empty.reps')}</p>
          {canWrite && (
            <div className="flex justify-center">
              <Button
                size="sm"
                onClick={() => setAssignOpen(true)}
                disabled={candidates.length === 0}
              >
                {t('campaign.assign_rep')}
              </Button>
            </div>
          )}
        </Card>
        {modal}
      </>
    );
  return (
    <div className="space-y-3">
      {canWrite && (
        <div className="flex justify-end">
          <Button
            size="sm"
            onClick={() => setAssignOpen(true)}
            disabled={candidates.length === 0}
          >
            {t('campaign.assign_rep')}
          </Button>
        </div>
      )}
      <Card className="overflow-hidden p-0">
        <table className="w-full text-sm">
          <thead className="bg-muted text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-4 py-2 text-left">{t('campaign.table.rep_id')}</th>
              <th className="px-4 py-2 text-right">{t('campaign.table.account_quota')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={`${r.campaignId}-${r.repUserId}`} className="border-t">
                <td className="px-4 py-2">
                  {userById.get(r.repUserId)?.email ?? (
                    <span className="font-mono text-xs">{r.repUserId}</span>
                  )}
                </td>
                <td className="px-4 py-2 text-right">{r.quotaAccounts ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      {modal}
    </div>
  );
}

function AssignRepDialog({
  open,
  onClose,
  onAssigned,
  campaignId,
  candidates,
}: {
  open: boolean;
  onClose: () => void;
  onAssigned: () => void;
  campaignId: string;
  candidates: UserSummary[];
}) {
  const { t } = useTranslation();
  const [repUserId, setRepUserId] = useState('');
  const [quota, setQuota] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!repUserId) return;
    setSubmitting(true);
    setError(null);
    try {
      const body: Record<string, unknown> = { repUserId };
      if (quota.trim()) body.quotaAccounts = parseInt(quota, 10);
      await apiClient.post(`/campaigns/${campaignId}/assignments`, body);
      onAssigned();
      setRepUserId('');
      setQuota('');
      onClose();
    } catch {
      setError(t('assignRep.error_generic'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg border bg-card p-6 shadow-xl">
        <h3 className="mb-4 text-lg font-semibold">{t('assignRep.title')}</h3>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="space-y-1">
            <label className="text-sm font-medium">{t('assignRep.rep')}</label>
            <select
              value={repUserId}
              onChange={(e) => setRepUserId(e.target.value)}
              className="w-full rounded-md border bg-transparent px-3 py-2 text-sm"
              required
            >
              <option value="">—</option>
              {candidates.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.email} ({u.role})
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium">{t('assignRep.quota')}</label>
            <input
              type="number"
              min="0"
              value={quota}
              onChange={(e) => setQuota(e.target.value)}
              className="w-full rounded-md border bg-transparent px-3 py-2 text-sm"
            />
          </div>
          {error && (
            <div className="rounded-md border border-red-800 bg-red-950/40 px-3 py-2 text-sm text-red-300">
              {error}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" size="sm" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" size="sm" disabled={!repUserId || submitting}>
              {submitting ? t('assignRep.assigning') : t('assignRep.submit')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
